<template>
    <v-modal :is-open="isOpen"
        class="bg-white"
        @close="$emit('close')">
        <v-modal-header @close="$emit('close')">
            {{ t('availability-title') }}
        </v-modal-header>

        <div class="space-y-lg px-lg pb-20">
            <p class="text-base tracking-xl">
                {{ t('availability-model-content') }}
            </p>

            <v-info theme="blue"
                class="flex justify-between space-x-sm">
                <span class="w-md text-2xl">
                    <v-icon icon="info-circle"
                        class="text-xl"></v-icon>
                </span>

                <p v-if="content !== undefined"
                    class="grow text-sm tracking-xl">
                    {{ content.content[0].text }}
                </p>
            </v-info>

            <div class="">
                <div v-if="outlets.length === 0"
                    class="flex flex-col items-center justify-center py-lg">
                    <v-spinner></v-spinner>

                    <p>
                        {{ t('loading') }}
                    </p>
                </div>

                <div v-if="productsForPurchase.length > 0">
                    <div class="py-lg">
                        <v-h4>{{ t('title-available') }}</v-h4>

                        <div class="mt-md flex flex-row">
                            <div class="flex flex-row items-center pr-lg">
                                <div class="mr-sm size-3 rounded-full bg-green-500"></div>
                                {{ t('available-more-than-five') }}
                            </div>

                            <div class="flex flex-row items-center">
                                <div class="mr-sm size-3 rounded-full bg-yellow-500"></div>
                                {{ t('available-less-than-five') }}
                            </div>
                        </div>
                    </div>

                    <template v-for="outlet in productsForPurchase"
                        :key="outlet.id">
                        <v-page-product-availability-outlet
                            show-ball
                            :outlet="outlet"
                            :is-buyable="isBuyable" />
                    </template>
                </div>

                <div v-if="modelProducts.length > 0">
                    <div class="py-lg">
                        <v-h4>{{ t('title-model') }}</v-h4>
                    </div>

                    <template v-for="outlet in modelProducts"
                        :key="outlet.id">
                        <v-page-product-availability-outlet
                            :outlet="outlet"
                            :is-buyable="isBuyable" />
                    </template>
                </div>

                <div v-if="orderableProducts.length > 0">
                    <div class="py-lg">
                        <v-h4>{{ t('title-orderable') }}</v-h4>
                    </div>

                    <template v-for="outlet in orderableProducts"
                        :key="outlet.id">
                        <v-page-product-availability-outlet
                            :outlet="outlet"
                            :is-buyable="isBuyable" />
                    </template>
                </div>
            </div>
        </div>
    </v-modal>
</template>

<script lang="ts" setup>
import type { OutletData } from '../../../composables/use-outlet-stocks.ts';

const properties = defineProps<{
    isOpen: boolean
    outlets: OutletData[]
    isBuyable: boolean
}>();

defineEmits<{
    (event: 'close'): void
}>();

const { t } = useI18n();

const outlets = computed(() => properties.outlets);

const outletData = ref<OutletData[]>([]);

watch(outlets, () => {
    outletData.value = outlets.value;
});

const filterOutlets = computed(() => outlets?.value.filter((outlet) => !/varasto/i.test(outlet.title)));

const contents = useStoryblokStory('global/store-availability-ingress');
const content = computed(() => contents.value?.content.content.content[0]);

const modelProducts = computed(() => filterOutlets.value.filter((outlet) => outlet.modelQuantity > 0));
const productsForPurchase = computed(() => filterOutlets.value.filter((outlet) => outlet.availableQuantity >= 1));
// eslint-disable-next-line max-len
const orderableProducts = computed(() => filterOutlets.value.filter((outlet) => properties.isBuyable && outlet.modelQuantity === 0 && outlet.availableQuantity === 0));
</script>

<!-- eslint-disable max-len -->
<i18n lang="json">
{
    "et": {
            "choose-outlet": "Valige kauplus",
            "availability-title": "Saadavus",
            "availability-model-content": "Kõiki tooteid saab osta igast kauplustest. Alati sama soodsad hinnad nii e-poes kui ka kauplustes!",
            "availability-model-info": "Paljud tooted saadavad erinevates värvides ning mudelites. Toote näidis võib kaupluste lõikes erineda.",
            "loading": "Laen kauplusi..",

            "availability-buy-now": "Kohe laos ja näidis olemas",
            "availability-few-left": "Veel viimased laos ja näidis olemas",
            "availability-buy-now-no-model": "Kohe kaupluse laost saadaval",
            "model-order-from-store": "Näidis olemas ja kauplusest tellitav ",
            "temporarily-out-of-stock": "Kauplusest tellitav",
            "not-available": "Toode pole saadaval",
            "ask-from-store": "Küsi kauplusest!",

            "title-model": "Veel viimased laos ja näidis olemas",
            "title-orderable": "Kauplusest tellitav",
            "title-available": "Kohe laos",
            "available-more-than-five": "Rohkem kui 5 tk",
            "available-less-than-five": "Alla 5 tk"
    },
        "fi": {
            "choose-outlet": "Valitse myymälä",
            "availability-title": "Saatavuus",
            "availability-model-content": "Kaikki tuotteet ostettavissa kaikista myymälöistä. Verkossa ja myymälöissä aina samat edulliset hinnat!",
            "availability-model-info": "Monista tuotteista on saatavilla lukuisia eri värejä ja malleja. Tuotteesta voi olla nähtäväillä eri versio myymälässäsi",
            "loading": "Ladataan myymälöitä..",

            "availability-buy-now": "Heti varastosta ja malli esillä",
            "availability-few-left": "Muutama jäljellä ja malli esillä",
            "availability-buy-now-no-model": "Heti myymälän varastosta",
            "model-order-from-store": "Malli esillä ja tilattavissa myymälästä",
            "temporarily-out-of-stock": "Tilattavissa myymälästä",
            "not-available": "Ei saatavilla",
            "ask-from-store": "Kysy saatavuus myymälästä",

            "title-model": "Mallikappale esillä",
            "title-orderable": "Tilattavissa",
            "title-available": "Heti mukaan",
            "available-more-than-five": "Yli 5 kpl",
            "available-less-than-five": "Alle 5 kpl"
        }
}
</i18n>
